import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/auth/AuthContext';
import { reviewsApi } from '@/api/reviews';
import { ApiHttpError, ApiNetworkError } from '@/api/client';

const TAG_KEYS = [
  'reviews.tags.worth_queue',
  'reviews.tags.bring_friend',
  'reviews.tags.cash_only',
  'reviews.tags.spicy',
  'reviews.tags.late_night',
  'reviews.tags.hidden_gem',
] as const;

export function ReviewsRoute() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const toast = useToast();
  const auth = useAuth();
  const [rating, setRating] = useState<0 | 1 | 2 | 3 | 4 | 5>(0);
  const [body, setBody] = useState('');
  const [tags, setTags] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const toggleTag = (key: string) => {
    setTags((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const onPost = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (submitting || rating === 0 || !id) return;
    if (!auth.user) {
      toast.show(t('reviews.sign_in_required'));
      return;
    }
    const trimmedBody = body.trim();
    const origin = e.currentTarget;
    setSubmitting(true);
    try {
      const token = await auth.getToken();
      if (!token) {
        toast.show(t('reviews.sign_in_required'));
        setSubmitting(false);
        return;
      }
      const lang = i18n.language.startsWith('th') ? 'th' : 'en';
      await reviewsApi.submit(
        {
          stall_id: id,
          rating,
          ...(trimmedBody ? { body: trimmedBody } : {}),
          language: lang,
        },
        token,
      );
      const points = 10;
      toast.burst(origin, points);
      toast.show(t('reviews.success'), points);
      setTimeout(() => nav(`/stall/${id}`), 1100);
    } catch (err) {
      const msg =
        err instanceof ApiHttpError
          ? err.message
          : err instanceof ApiNetworkError
            ? t('reviews.network_error')
            : err instanceof Error
              ? err.message
              : t('reviews.network_error');
      toast.show(msg);
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-[560px] px-4 pb-16 pt-6">
      <h1 className="h1 m-0 mb-6">{t('reviews.title')}</h1>

      <fieldset className="mb-4">
        <legend className="mb-2 text-sm font-semibold text-ink-soft">{t('reviews.rating_label')}</legend>
        <div className="flex justify-center gap-1.5 py-3" role="radiogroup">
          {[1, 2, 3, 4, 5].map((v) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={rating >= v}
              onClick={() => setRating(v as 1 | 2 | 3 | 4 | 5)}
              className="grid h-10 w-10 cursor-pointer place-items-center border-0 bg-transparent"
              aria-label={`${v} star${v === 1 ? '' : 's'}`}
            >
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill={rating >= v ? 'var(--soi-500)' : 'transparent'}
                stroke={rating >= v ? 'var(--soi-700)' : 'var(--soi-500)'}
                style={{ transition: 'fill var(--t-fast) var(--ease-out)' }}
              >
                <path d="M12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61z" />
              </svg>
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="mb-4">
        <legend className="mb-2 text-sm font-semibold text-ink-soft">{t('reviews.tags_label')}</legend>
        <div className="flex flex-wrap gap-2">
          {TAG_KEYS.map((key) => {
            const selected = tags.has(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggleTag(key)}
                aria-pressed={selected}
                className={[
                  'min-h-[36px] rounded-pill border px-3.5 py-2 text-xs font-semibold',
                  selected
                    ? 'border-soi-600 bg-soi-600 text-white'
                    : 'border-[color:var(--border)] bg-[color:var(--surface)] text-soi-700',
                ].join(' ')}
              >
                {t(key)}
              </button>
            );
          })}
        </div>
      </fieldset>

      <fieldset className="mb-4">
        <label htmlFor="body" className="mb-2 block text-sm font-semibold text-ink-soft">
          {t('reviews.body_label')}
        </label>
        <textarea
          id="body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t('reviews.body_placeholder')}
          className="min-h-[140px] w-full resize-y rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] p-3 text-base"
          style={{
            outline: 'none',
            transition: 'border-color var(--t-fast) var(--ease-out)',
          }}
        />
        <p className="mt-1 text-[12px] text-ink-muted">{t('reviews.points_note')}</p>
      </fieldset>

      <div className="mt-6 flex items-center justify-between gap-3">
        <Link
          to={id ? `/stall/${id}` : '/'}
          className="inline-flex min-h-[44px] items-center justify-center rounded-md px-4 py-2 text-sm font-semibold text-ink-soft hover:bg-soi-100"
        >
          {t('reviews.cancel')}
        </Link>
        <button
          type="button"
          onClick={(e) => void onPost(e)}
          disabled={rating === 0 || submitting}
          className="inline-flex min-h-[52px] items-center justify-center rounded-md bg-soi-600 px-6 py-3 text-base font-bold text-white hover:bg-soi-700 disabled:opacity-50"
        >
          {submitting ? t('reviews.posting') : t('reviews.post')}
        </button>
      </div>
    </div>
  );
}
